import { pino } from 'pino'

const logger = pino({
  level: process.env.LOG_LEVEL || 'info',
  name: 'error-deserializer',
})

/**
 * The type of expected application errors
 */
export enum ApplicationErrorName {
  INVALID_ARGUMENT = 'invalid_argument',
  PERMISSION_ERROR = 'permission_error',
  NOT_FOUND_ERROR = 'not_found_error',
  STATE_ERROR = 'state_error',
  MISSING_VALUE = 'missing_value',
  INTERNAL_SERVER_ERROR = 'internal_server_error',
  THIRD_PARTY_ERROR = 'third_party_error',
}

/**
 * The HTTP status codes for the application error types
 */
const CODES_FOR: Record<ApplicationErrorName, { code: number; statusText: string }> = {
  /**
   * The request was invalid or cannot be otherwise served.
   */
  [ApplicationErrorName.INVALID_ARGUMENT]: { code: 400, statusText: 'Bad Request' },
  /**
   * The request was forbidden.
   */
  [ApplicationErrorName.PERMISSION_ERROR]: { code: 403, statusText: 'Forbidden' },
  [ApplicationErrorName.NOT_FOUND_ERROR]: { code: 404, statusText: 'Not Found' },
  /**
   * The request could not be processed because of conflict in the current state of the resource.
   */

  [ApplicationErrorName.STATE_ERROR]: { code: 409, statusText: 'Conflict' },
  /**
   * The server understood the request but cannot process it.
   */
  [ApplicationErrorName.MISSING_VALUE]: { code: 422, statusText: 'Unprocessable Entity' },
  /**
   * The server encountered an unexpected condition that prevented it from fulfilling the request.
   */
  [ApplicationErrorName.INTERNAL_SERVER_ERROR]: { code: 500, statusText: 'Internal Server Error' },
  /**
   * The server was acting as a gateway or proxy to a third party service and received an invalid response from the upstream server.
   */
  [ApplicationErrorName.THIRD_PARTY_ERROR]: { code: 502, statusText: 'Bad Gateway' },
}

type _ApplicationError = {
  message: string
  type: ApplicationErrorName
  info?: Record<string, unknown>
}

/**
 * A custom error class that works well in an RPC context.
 * Uses the message and name to identify the error which are serializable fields.
 */
export class ApplicationError extends Error {
  /**
   * @param message the error message, this must be informative but generic since it will be
   * returned to the client
   * @param details the error details, this will be used to identify the error and provide
   * more information to the client. Any of the info fields are for internal use only (logging, etc).
   */
  constructor(message: string, details: { type: ApplicationErrorName; info?: Record<string, unknown> }) {
    super(JSON.stringify({ message, ...details }))
    this.name = 'ApplicationError'
  }

  getDetails() {
    const { message, ...details }: _ApplicationError = JSON.parse(this.message)
    return details
  }
}

export const MissingValueError = (message: string, info?: Record<string, unknown>) =>
  new ApplicationError(message, { type: ApplicationErrorName.MISSING_VALUE, info })

export const ThirdPartyError = (message: string, info?: Record<string, unknown>) =>
  new ApplicationError(message, { type: ApplicationErrorName.THIRD_PARTY_ERROR, info })

export const InvalidArgumentError = (message: string, info?: Record<string, unknown>) =>
  new ApplicationError(message, { type: ApplicationErrorName.INVALID_ARGUMENT, info })

export const NotFoundError = (message: string, info?: Record<string, unknown>) =>
  new ApplicationError(message, { type: ApplicationErrorName.NOT_FOUND_ERROR, info })

export const StateError = (message: string, info?: Record<string, unknown>) =>
  new ApplicationError(message, { type: ApplicationErrorName.STATE_ERROR, info })

export const PermissionError = (message: string, info?: Record<string, unknown>) =>
  new ApplicationError(message, { type: ApplicationErrorName.PERMISSION_ERROR, info })

export const ErrorDeserializer = (error: Error): ApplicationError => {
  if (error.message.includes('ApplicationError: ')) {
    // something has changed in how RPC errors are serialized
    const { message, type, info }: _ApplicationError = JSON.parse(error.message.replace('ApplicationError: ', ''))
    return new ApplicationError(message, { type, info })
  }
  // for any uncaught errors, return an internal server error and make sure not to bubble up the error to the client
  logger.error('Unknown error encountered', { error: error.message })
  return new ApplicationError('Internal Server Error', { type: ApplicationErrorName.INTERNAL_SERVER_ERROR })
}

export type ResponseError = {
  message: string
  statusCode: number
  statusText: string
}

const ResponseErrorFrom = (applicationError: ApplicationError): ResponseError => {
  const appError: _ApplicationError = JSON.parse(applicationError.message)
  return {
    message: appError.message,
    statusCode: CODES_FOR[appError.type].code,
    statusText: CODES_FOR[appError.type].statusText,
  }
}

export const ResponseFrom = (applicationError: ApplicationError): Response => {
  const responseError = ResponseErrorFrom(applicationError)
  return new Response(JSON.stringify(responseError), { status: responseError.statusCode })
}
